import { FC, FormEvent, useEffect, useState } from 'react';
import { formOptions } from '@tanstack/react-form';
import { useAppForm } from '@new/common/hooks/form';
import { subscribeSchema } from '../schemas/validations';
import type { SubscribeFormValues } from '../schemas/validations';
import type { SubscriptionOption } from '../constants';
import { useSubscribe } from '../hooks/useSubscribe';
import { SectionCard } from './SectionCard';
import { PaymentInfo } from './PaymentInfo';
import { SubscriptionType } from './SubscriptionType';
import { EnrollmentSummary } from './EnrollmentSummary';
import { MasterPrerequisiteNotice } from './MasterPrerequisiteNotice';
import { ConfirmSubscribeModal } from './ConfirmSubscribeModal';
import { DegreeType } from '@teachur/http-client';

interface SubscribeFormProps {
  subscription: SubscriptionOption;
  degreeName?: string;
  degreeType?: string;
}

const buildFormOpts = (subscription: SubscriptionOption) =>
  formOptions({
    defaultValues: {
      billingName: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      code: '',
      subscriptionType: subscription.id,
    },
  });

const validate = ({ value }: { value: Record<string, unknown> }) => {
  const parsed = subscribeSchema.safeParse(value);
  if (parsed.success) {
    return { fields: {} };
  }

  const errors: Record<string, string> = {};
  parsed.error.issues.forEach((issue) => {
    const field = issue.path[0];
    if (typeof field === 'string' && !errors[field]) {
      errors[field] = issue.message;
    }
  });
  return { fields: errors };
};

export const SubscribeForm: FC<SubscribeFormProps> = ({ subscription, degreeName, degreeType }) => {
  const { price, subPrice, isFreeSubscription, isSubmitting, applyCoupon, submit } =
    useSubscribe(subscription);

  // Holds the validated form values between clicking "Register" (which opens the
  // confirmation modal) and confirming (which actually charges the card).
  const [pendingValues, setPendingValues] = useState<SubscribeFormValues | null>(null);

  const form = useAppForm({
    ...buildFormOpts(subscription),
    validators: {
      onChange: validate,
      onSubmit: ({ value }) => {
        const result = validate({ value });
        if (Object.keys(result.fields).length > 0) {
          return result;
        }
        // Don't charge yet — stage the values and open the confirmation modal.
        setPendingValues(subscribeSchema.parse(value));
      },
    },
  });

  useEffect(() => {
    form.setFieldValue('subscriptionType', subscription.id);
  }, [subscription.id, form]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  const handleConfirm = () => {
    if (pendingValues) {
      submit(pendingValues);
    }
  };

  const closeConfirm = () => {
    if (!isSubmitting) {
      setPendingValues(null);
    }
  };

  const handleCouponApplied = (code: string, newPrice: number, newMonthlyPrice: number) => {
    form.setFieldValue('code', code);
    applyCoupon(newPrice, newMonthlyPrice);
  };

  return (
    <form
      onSubmit={onSubmit}
      className="flex min-h-full flex-col gap-6 [&_input[data-slot=input]]:h-[48px]! [&_input[data-slot=input]]:md:h-[48px]! [&_input[data-slot=input]]:xl:h-[48px]!"
    >
      <EnrollmentSummary degreeName={degreeName} degreeType={degreeType} />

      {degreeType === DegreeType.Master && <MasterPrerequisiteNotice />}

      <SectionCard>
        <div className="flex flex-col gap-5">
          <h3 className="[font-size:var(--fs-l1)]! [font-family:var(--font-montserrat)]! text-(--neutral-black-NB1000)! m-0!">
            Payment Details
          </h3>

          <div className="flex flex-col">
            <form.AppField
              name="billingName"
              children={(field) => <field.Text label="Full Name" placeholder="Full Name" />}
            />

            <div className="flex flex-col lg:flex-row justify-between gap-5">
              <form.AppField
                name="address"
                children={(field) => (
                  <field.Text label="Address" placeholder="Address" className="flex-1" />
                )}
              />
              <form.AppField
                name="address2"
                children={(field) => (
                  <field.Text label="Address 2" placeholder="Address 2" className="flex-1" />
                )}
              />
            </div>

            <div className="flex flex-col lg:flex-row justify-between gap-5">
              <form.AppField
                name="country"
                children={(field) => (
                  <field.Text label="Country" placeholder="Country" className="flex-1" />
                )}
              />
              <form.AppField
                name="state"
                children={(field) => (
                  <field.Text label="State" placeholder="State" className="flex-1" />
                )}
              />
            </div>

            <div className="flex flex-col lg:flex-row justify-between gap-5">
              <form.AppField
                name="city"
                children={(field) => (
                  <field.Text label="City" placeholder="City" className="flex-1" />
                )}
              />
              <form.AppField
                name="zip"
                children={(field) => (
                  <field.Text label="Zip" placeholder="Zip" className="flex-1" />
                )}
              />
            </div>
          </div>
        </div>
      </SectionCard>

      {!isFreeSubscription && (
        <>
          <SectionCard>
            <form.AppField
              name="code"
              children={(field) => (
                <PaymentInfo code={field.state.value} onCouponApplied={handleCouponApplied} />
              )}
            />
          </SectionCard>

          <SectionCard>
            <SubscriptionType subscriptionId={subscription.id} price={price} subPrice={subPrice} />
          </SectionCard>
        </>
      )}

      <div className="flex justify-center">
        <form.AppForm>
          <form.SubmitButton label={isSubmitting ? 'Registering...' : 'Register'} />
        </form.AppForm>
      </div>

      <ConfirmSubscribeModal
        isOpen={pendingValues !== null}
        degreeName={degreeName}
        price={price}
        subPrice={subPrice}
        isSubmitting={isSubmitting}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
      />
    </form>
  );
};
